/**
 * The preview of a SEGMENT list's DRAFT: the count that would be mailed, the
 * breakdown of who is left out and why, and every eligible person, paged, with
 * a leading checkbox per row. Ticked = included; unticking excludes that PERSON
 * RECORD from this list. Exclusions are part of the draft and are saved by the
 * page's Save button with the rules.
 *
 * ⚠️ The boxes render from the DRAFT set, never from the row's `excludedByHand`,
 * so a toggle shows at once; the page re-requests the preview behind it.
 *
 * ⚠️ The header box acts on EVERYONE matching the search and the Show filter,
 * across every page — not only the rows on screen. When that is more than this
 * page, a confirmation names the number first, and the refs come from a
 * `refsOnly` request rather than from the rows.
 *
 * This component fetches nothing on its own except that refs list, through the
 * `loadMatchingRefs` callback the page builds from the request it last showed.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Input,
  Panel,
  PanelBody,
  PanelHeader,
  SegmentedControl,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { EmailLink } from '@/components/ui/TextLink';
import { ApiError } from '@/lib/api';
import { cx } from '@/lib/cx';
import type { ListPreview, ListPreviewRow, ListPreviewShow, MarketingMemberRef } from '@/lib/types';
import { Pager } from './ListMembersTable';

/** The server's cap on a list's stored exclusions. */
const MAX_EXCLUDED_MEMBERS = 10000;

/** `type:id` — a contact and a subscriber can share an id. The page keys its draft the same way. */
const memberKey = (m: { type: string; id: number }) => `${m.type}:${m.id}`;

const SHOW_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'included', label: 'Included' },
  { value: 'excluded', label: 'Excluded' },
] as const satisfies readonly { value: ListPreviewShow; label: string }[];

function peopleNoun(n: number): string {
  return n === 1 ? 'person' : 'people';
}

export function SegmentPreviewTable({
  preview,
  loading,
  error,
  excluded,
  search,
  onSearch,
  appliedSearch,
  show,
  onShow,
  appliedShow,
  page,
  perPage,
  onPage,
  readOnly,
  onToggle,
  onApplyMany,
  loadMatchingRefs,
  matchesEveryone = false,
}: {
  /**
   * True when the DRAFT definition has no rules, so the segment matches every
   * eligible person. Shown as a notice above the count; the page passes it.
   */
  matchesEveryone?: boolean;
  preview: ListPreview | null;
  loading: boolean;
  error: string | null;
  /** The DRAFT exclusion set, keyed `type:id`. */
  excluded: ReadonlySet<string>;
  search: string;
  onSearch(value: string): void;
  /** The search the preview on screen was computed for. */
  appliedSearch: string;
  show: ListPreviewShow;
  onShow(value: ListPreviewShow): void;
  /** The Show filter the preview on screen was computed for. */
  appliedShow: ListPreviewShow;
  page: number;
  perPage: number;
  onPage(page: number): void;
  readOnly: boolean;
  onToggle(ref: MarketingMemberRef, include: boolean): void;
  onApplyMany(refs: MarketingMemberRef[], include: boolean): void;
  /** Every ref matching the preview on screen (a `refsOnly` request). */
  loadMatchingRefs(signal: AbortSignal): Promise<MarketingMemberRef[]>;
}) {
  // An older server sends no `rows`: fall back to the sample, all included.
  const rows: ListPreviewRow[] =
    preview?.rows ?? (preview?.sample ?? []).map((m) => ({ ...m, excludedByHand: false }));
  const total = preview?.total ?? preview?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const includedOnPage = rows.filter((r) => !excluded.has(memberKey(r))).length;
  const allIncluded = rows.length > 0 && includedOnPage === rows.length;
  const someIncluded = includedOnPage > 0 && includedOnPage < rows.length;
  // Clicking a fully ticked header excludes; anything else includes.
  const headerIncludes = !allIncluded;

  const headerRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (headerRef.current) headerRef.current.indeterminate = someIncluded;
  }, [someIncluded, rows.length]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmInclude, setConfirmInclude] = useState(true);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const confirmController = useRef<AbortController | null>(null);

  useEffect(() => () => confirmController.current?.abort(), []);

  const applyChecked = (refs: MarketingMemberRef[], include: boolean): string | null => {
    if (!include) {
      const added = refs.filter((r) => !excluded.has(memberKey(r))).length;
      if (excluded.size + added > MAX_EXCLUDED_MEMBERS) {
        return `A list can exclude at most ${MAX_EXCLUDED_MEMBERS.toLocaleString()} people.`;
      }
    }
    onApplyMany(refs, include);
    return null;
  };

  const onHeader = () => {
    if (readOnly || rows.length === 0) return;
    if (total <= rows.length) {
      const refs = rows.map((r) => ({ type: r.type, id: r.id }));
      setInlineError(applyChecked(refs, headerIncludes));
      return;
    }
    setInlineError(null);
    setConfirmInclude(headerIncludes);
    setConfirmError(null);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    confirmController.current?.abort();
    confirmController.current = null;
    setConfirmLoading(false);
    setConfirmOpen(false);
    setConfirmError(null);
  };

  const confirm = async () => {
    if (confirmLoading) return;
    const controller = new AbortController();
    confirmController.current = controller;
    setConfirmLoading(true);
    setConfirmError(null);
    try {
      const refs = await loadMatchingRefs(controller.signal);
      if (controller.signal.aborted) return;
      const refusal = applyChecked(refs, confirmInclude);
      if (refusal) {
        setConfirmError(refusal);
        return;
      }
      closeConfirm();
    } catch (err) {
      if (controller.signal.aborted) return;
      setConfirmError(
        err instanceof ApiError ? (err.formMessage ?? err.message) : 'The matching people could not be listed.',
      );
    } finally {
      if (!controller.signal.aborted) setConfirmLoading(false);
    }
  };

  const confirmScope = appliedSearch ? ` matching “${appliedSearch}”` : '';
  const confirmView =
    appliedShow === 'included' ? ' in the Included view' : appliedShow === 'excluded' ? ' in the Excluded view' : '';
  const confirmTitle = `${confirmInclude ? 'Include' : 'Exclude'} ${total.toLocaleString()} ${peopleNoun(total)}${confirmScope}?`;

  const breakdown = preview?.excluded;
  const breakdownRows: [string, number][] = breakdown
    ? [
        ['Opted out', breakdown.optedOut],
        ['Suppressed', breakdown.suppressed],
        ['No email', breakdown.noEmail],
        ['Invalid email', breakdown.invalidEmail],
        ...(breakdown.byHand !== undefined ? ([['Excluded by hand', breakdown.byHand]] as [string, number][]) : []),
      ]
    : [];

  return (
    <Panel className="min-w-0">
      <PanelHeader title="Preview" />
      <PanelBody className="grid gap-4">
        {matchesEveryone && (
          <Notice tone="warning" role="status" title="No rules: this list matches everyone" />
        )}
        {error && (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        )}
        {preview === null ? (
          error ? null : (
            <div className="grid gap-2">
              <Skeleton className="h-10 w-32" />
              <Skeleton className="h-24 w-full" />
            </div>
          )
        ) : (
          <div className="grid gap-4 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-start sm:gap-8">
            <div aria-live="polite" className={loading ? 'opacity-60' : undefined}>
              <p className="m-0 text-3xl font-semibold text-fg tabular">{preview.count.toLocaleString()}</p>
              <p className="m-0 text-sm text-fg-muted">
                {preview.count === 1 ? 'recipient' : 'recipients'} would be mailed
              </p>
            </div>
            <dl className="m-0 grid max-w-sm grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-sm">
              {breakdownRows.map(([label, value]) => (
                <div key={label} className="contents">
                  <dt className="text-fg-secondary">{label}</dt>
                  <dd className="m-0 text-right tabular text-fg">{value.toLocaleString()}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <Input
            type="search"
            aria-label="Search people"
            placeholder="Search name, email or company"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            className="min-w-0 flex-1 sm:max-w-sm"
          />
          <SegmentedControl label="Show" value={show} options={SHOW_OPTIONS} onChange={onShow} size="sm" />
        </div>
        {inlineError && (
          <Notice tone="danger" role="alert">
            {inlineError}
          </Notice>
        )}
      </PanelBody>

      {preview === null ? (
        error ? null : (
          <div className="grid gap-2 px-4 pb-4">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        )
      ) : rows.length === 0 ? (
        <EmptyState
          title={
            appliedSearch
              ? 'Nobody matches this search'
              : appliedShow === 'excluded'
                ? 'Nobody is excluded by hand'
                : appliedShow === 'included'
                  ? 'Nobody is included'
                  : 'Nobody matches this definition'
          }
          description={appliedSearch ? 'Try a different search.' : 'Change the rules above to widen the list.'}
        />
      ) : (
        <TableWrap
          frozenHeader
          frozenMaxHeight="min(60vh, 560px)"
          className={loading ? 'opacity-60' : undefined}
        >
          <Table className="min-w-[640px]">
            <THead>
              <TR>
                <TH className="w-10">
                  <Checkbox
                    ref={headerRef}
                    label={
                      <span className="sr-only">
                        {headerIncludes ? 'Include' : 'Exclude'} everyone matching
                      </span>
                    }
                    checked={allIncluded}
                    disabled={readOnly}
                    onChange={onHeader}
                  />
                </TH>
                <TH>Name</TH>
                <TH>Email</TH>
                <TH>Company</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((row) => {
                const key = memberKey(row);
                const included = !excluded.has(key);
                return (
                  <TR key={key}>
                    <TD>
                      <Checkbox
                        label={<span className="sr-only">Include {row.email}</span>}
                        checked={included}
                        disabled={readOnly}
                        onChange={() => onToggle({ type: row.type, id: row.id }, !included)}
                      />
                    </TD>
                    <TD
                      className={cx('max-w-[16rem] truncate', !included && 'text-fg-muted')}
                      title={row.name || undefined}
                    >
                      {row.name || '—'}
                    </TD>
                    <TD className={cx('max-w-[18rem] truncate', !included && 'text-fg-muted')} title={row.email}>
                      <EmailLink address={row.email} tone={included ? 'default' : 'muted'} />
                    </TD>
                    <TD className="max-w-[16rem] truncate" title={row.companyName ?? undefined}>
                      {row.companyId !== null ? (
                        <Link
                          to={`/companies/${row.companyId}`}
                          className="text-fg underline-offset-2 hover:underline"
                        >
                          {row.companyName || `Company #${row.companyId}`}
                        </Link>
                      ) : (
                        (row.companyName ?? '—')
                      )}
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        </TableWrap>
      )}

      {preview !== null && (
        <Pager
          page={page}
          totalPages={totalPages}
          total={total}
          shown={rows.length}
          noun={{ singular: 'person', plural: 'people' }}
          onPage={onPage}
        />
      )}

      <ConfirmDialog
        open={confirmOpen}
        onClose={closeConfirm}
        onConfirm={confirm}
        title={confirmTitle}
        description={`All ${total.toLocaleString()} ${peopleNoun(total)}${confirmScope}${confirmView}, on every page, will be ${
          confirmInclude ? 'ticked' : 'unticked'
        }. Nothing is saved until you press Save.`}
        confirmLabel={confirmInclude ? 'Include' : 'Exclude'}
        tone={confirmInclude ? 'primary' : 'danger'}
        loading={confirmLoading}
        error={confirmError}
      />
    </Panel>
  );
}
